---
title: "What Choosie can do"
description: "A map of Choosie's capabilities in Chosen — candidates, jobs, application stages, feedback, and scheduling — plus an honest list of what it can't do."
category: "hq"
keywords:
  - "Choosie capabilities"
  - "what can Choosie do"
  - "AI assistant features ATS"
  - "Choosie tools"
schema: "HowTo"
nav:
  section: "choosie"
  order: 30
  parent: "/choosie"
---

[Choosie](/choosie) can call 40-plus tools that reach into Chosen. This page groups them so you know what to ask for. Remember the split: looking things up runs immediately, while anything that creates, updates, deletes, or sends is held back as a [plan you approve](/choosie/plans-and-approvals) first.

## Candidates

- **Search and list.** Find candidates by a plain-language query, or list everyone on a job.
- **Look up.** Pull a candidate's full profile, their notes, their feedback.
- **Create and update.** Add a new candidate, or change fields — name, email, position, source, company — on an existing one.
- **Notes.** Add a note to a candidate, or read the existing ones.
- **Resumes and imports.** Attach a resume file to your message and Choosie can put it on a candidate, parsed through the normal [resume pipeline](/guides/candidates/resume-parsing). Attach a spreadsheet and it can [bulk-create candidates](/guides/candidates/bulk-import-and-export) from the rows.
- **Delete.** Remove a candidate. This one is permanent — see the limits below.

## Jobs

- **List and look up.** See your jobs, open or closed, and read the detail on any one.
- **Create and update.** Draft a new job, or edit an existing one — title, description, department, location, compensation, and more.
- **Open and close.** Change a job's status.
- **Screening questions.** Read or replace the [application questions](/guides/careers/application-questions) on a job.
- **Descriptions.** Generate or sharpen a job description with AI.

## Application stages

- **Connect a candidate to a job**, creating an application — and remove that link.
- **Move the stage.** Advance or move an application through your [pipeline](/guides/candidates/statuses-and-pipeline) — lead, applied, screening, interview, offer, hired, rejected, or a custom interview stage.

## Feedback

- **Read feedback** left on a candidate.
- **Add feedback** with a hiring recommendation on the 1-4 scale, from Strong No Hire to Strong Hire — see [match rating](/guides/candidates/match-rating) for how evaluation fits the rest of the picture.

## Scheduling

- **See interviews.** This week's scheduled meetings and what's still unscheduled.
- **Check availability** against interviewers' connected Google Calendars.
- **Create a scheduling request**, which sends the [scheduling email](/guides/scheduling/ai-scheduler-overview) to candidates.
- **Update or cancel a meeting** — change its time, notes, or interviewers.
- **Work the AI events queue.** Review, edit, approve, or reject the [AI-drafted scheduling replies](/guides/scheduling/ai-events-tab).

## Analytics, in chat

- **Charts on demand.** Skill distributions, the pipeline funnel for an open job, a match-score histogram, activity over the last 90 days — drawn inline next to the answer.
- **Novel charts and cross-tabs.** When the question doesn't fit a built-in tool, Choosie composes one — "avg match score by skill," "candidates by skill broken down by stage," stage-by-source heatmaps. Stacked bar, grouped bar, area, scatter, and heatmap are all on the menu. See [charts and tables in Choosie](/choosie/charts-and-tables#novel-charts-on-demand).
- **Tables for comparisons and rankings.** A side-by-side compare of two to ten candidates, or a ranked table over any dimension you can chart.
- **Custom-field reporting.** Anything your org tracks on candidates via [custom fields](/guides/candidates/custom-fields) can be a chart axis or a table column.
- **PNG and CSV export** from any chart or table card. See [charts and tables in Choosie](/choosie/charts-and-tables) for the full list.

## What it can't (or shouldn't) do

Choosie is genuinely capable. It is not a button for everything.

- **It acts with your permissions.** Choosie runs as you, with your [role](/getting-started/accounts-and-roles). If your role can't delete jobs or manage the org, neither can Choosie on your behalf. It's also scoped to your organization and never reaches another company's data.
- **Some things are faster to click.** Dragging one candidate between two columns on the board is quicker than describing it. Choosie shines on searches, multi-step changes, and questions that span records — not on single actions a click already handles.
- **An approved change has no one-click undo.** Deleting a candidate or a job removes associated data. A sent scheduling email is sent. Review the [plan](/choosie/plans-and-approvals) before approving.
- **It works from specific requests.** Choosie is not a mind reader. Give it the criteria, the names, the stage — [good prompts](/choosie/good-prompts) get good results.
- **It doesn't make the hiring decision.** Choosie moves data and answers questions. Whether to advance a candidate is still yours.

<Callout type="info" title="Connected integrations decide what scheduling Choosie can do">
Choosie's scheduling tools lean on your integrations. Checking calendar availability needs [Google Calendar](/getting-started/integrations/google-calendar) connected; sending a scheduling request needs [Gmail](/getting-started/integrations/gmail). Without them, Choosie will tell you the integration is missing rather than fail quietly.
</Callout>
